import { useCreateUser, useUpdateUser } from "@/services/users/mutation";
import { CopyOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Form,
  Input,
  message,
  Modal,
  Select,
  Space,
} from "antd";
import { forwardRef, useImperativeHandle, useState } from "react";
import { User } from "../../../types/user";
import EmailFormItem from "../form/items/EmailFormItem";
import PasswordFormItem from "../form/items/PasswordFormItem";

const roles = [
  {
    value: "waiter",
    label: "Mesero",
  },
  {
    value: "kitchen",
    label: "Cocina",
  },
  {
    value: "cashier",
    label: "Cajero",
  },
  {
    value: "admin",
    label: "Admin",
  },
  {
    value: "auditor",
    label: "Auditor",
  },
];

export interface ModalCUUserRef {
  childFunction: (register?: User) => void;
}

export const ModalCUUser = forwardRef<ModalCUUserRef>((props, ref) => {
  const [open, setOpen] = useState<boolean>(false);
  const [id, setId] = useState<string>("");
  const [isEdit, setIsEdit] = useState(false);
  const [isLoadingButton, setIsLoadingButton] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const [form] = Form.useForm();

  const [alert, setAlert] = useState({
    visible: false,
    description: "",
  });

  const [summary, setSummary] = useState("");
  const [openSummary, setOpenSummary] = useState(false);

  const childFunction = (register?: User) => {
    setOpen(true);
    setAlert({ visible: false, description: "" });
    setIsEdit(false);
    setOpenSummary(false);
    setSummary("");

    if (register) {
      //si viene id es edit
      setId(register.id);
      setIsEdit(true);
      form.setFieldsValue({
        name: register.name,
        role: register.role,
        email: register.email,
        password: "",
      });
    } else {
      form.resetFields();
    }
  };

  useImperativeHandle(ref, () => ({
    childFunction,
  }));

  const createUserMutation = useCreateUser();
  const updateUserMutation = useUpdateUser();

  const buildUserSummary = (user: User) => {
    return `
  Usuario ${isEdit ? "actualizado" : "creado"} correctamente
  Nombre: ${user.name}
  Email: ${user.email}
  Contraseña: ${user.password ?? "********"}
  Rol: ${user.role === "admin" ? "Administrador" : "Normal"}
  `.trim();
  };

  const handleError = (error: any) => {
    console.log(error);

    let error_message = "Ups, algo salió mal. Intenta nuevamente.";

    if (typeof error?.response?.data?.message === "string") {
      error_message = error.response.data.message;
    } 
    
    messageApi.error(error_message);

    if (error?.response?.data?.success === false) {
      setAlert({
        visible: true,
        description: error_message,
      });
    }
  };

  const manage = async () => {
    setOpenSummary(false);
    setSummary("");
    setIsLoadingButton(true);
    setAlert({ visible: false, description: "" });

    const formValues = form.getFieldsValue() as User;

    if (isEdit) {
      formValues.id = id;
    }

    const mutation = isEdit ? updateUserMutation : createUserMutation;

    mutation.mutate(formValues, {
      onSuccess: () => {
        messageApi.success("Usuario/a guardado correctamente.");

        const summaryText = buildUserSummary(formValues);

        setSummary(summaryText);
        setOpenSummary(true);

        if (!isEdit) {
          form.resetFields();
        }

        /* setOpen(false); */
      },
      onError: handleError,
      onSettled: () => {
        setIsLoadingButton(false);
      },
    });
  };

  return (
    <Modal
      title={isEdit ? "Editar" : "Agregar"}
      centered
      open={open}
      onCancel={() => setOpen(false)}
      footer={null}
    >
      {contextHolder}

      <Form
        form={form}
        scrollToFirstError={{
          behavior: "smooth",
          block: "center",
          inline: "center",
        }}
        layout="vertical"
        onFinish={manage}
      >
        {/* Name */}
        <Form.Item
          name="name"
          validateTrigger="onBlur"
          label="Nombre"
          rules={[{ required: true, max: 20, whitespace: true, min: 2 }]}
        >
          <Input placeholder="Ingresa nombre" />
        </Form.Item>

        {/* Rol */}
        <Form.Item
          name="role"
          label="Rol"
          rules={[
            {
              required: true,
            },
          ]}
        >
          <Select placeholder="Selecciona rol" options={roles} />
        </Form.Item>

        {/* email */}
        <EmailFormItem required={true} />

        <PasswordFormItem
          name="password"
          required={!isEdit}
          label="Contraseña"
          placeholder="Ingresa contraseña"
        />

        {alert.visible && (
          <div className="mb-4">
            <Alert
              message="Error"
              description={alert.description}
              type="error"
              showIcon
            />
          </div>
        )}


        {openSummary && (
          <div className="relative flex-1 p-3 mb-5 text-sm whitespace-pre-line bg-primary/20 rounded-lg">
            {/* Botón copiar */}
            <div className="absolute top-2 right-2">
              <Button
                size="small"
                icon={<CopyOutlined />}
                className="flex items-center bg-transparent border-primary text-primary"
                onClick={() => {
                  navigator.clipboard.writeText(summary);
                  message.success("Copiado al portapapeles");
                }}
              >
                Copiar
              </Button>
            </div>

            {/* Contenido */}
            <div className="pr-20">{summary}</div>
          </div>
        )}

        {/* Button */}
        <Form.Item className="flex justify-end items-end">
          <Space size="middle">
            <Button danger type="text" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button type="primary" htmlType="submit" loading={isLoadingButton}>
              Guardar
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
});
